import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Chip, IconButton, CircularProgress, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, FormControlLabel,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  AddRounded, EventBusyRounded, DeleteRounded, CalendarTodayRounded, BeachAccessRounded, WarningAmberRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useHolidays } from '../../hooks/useHolidays';
import { useQueues } from '../../hooks/useQueues';
import { Holiday, HolidayGroup, groupHolidays, fmtHolidayDate, CheckTokensResult } from '../../api/holidayTypes';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const HolidaysPage: React.FC = () => {
  const { listHolidays, deleteHoliday } = useHolidays();
  const { listQueues } = useQueues();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [queues, setQueues] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [delTarget, setDelTarget] = useState<HolidayGroup | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setHolidays(await listHolidays()); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load holidays.'); }
    finally { setLoading(false); }
  }, [listHolidays]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { listQueues(1, 100).then((r) => setQueues(r.data.map((q) => ({ id: q.id, name: q.name })))).catch(() => {}); }, [listQueues]);

  const groups = groupHolidays(holidays);

  const doDelete = async () => {
    if (!delTarget) return;
    const g = delTarget; setDelTarget(null);
    try { await Promise.all(g.ids.map((id) => deleteHoliday(id))); setToast('Holiday deleted'); reload(); }
    catch (e: any) { setToast(e?.response?.data?.message || 'Failed to delete'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography color="text.secondary" sx={{ maxWidth: 520 }}>
          Mark days your queues are closed. Tokens on those days are flagged as <b>affected</b> so you can reschedule, cancel, or notify customers.
        </Typography>
        <Button variant="contained" startIcon={<AddRounded />} onClick={() => setAddOpen(true)}>Add holiday</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
      ) : groups.length === 0 ? (
        <Box sx={{ ...card, textAlign: 'center', py: 8 }}>
          <Box sx={{ width: 88, height: 88, borderRadius: '24px', bgcolor: colors.primaryLight, color: colors.primary, display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}>
            <BeachAccessRounded sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h3" sx={{ mb: 0.5 }}>No holidays yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>Add your first closure to keep customers informed.</Typography>
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => setAddOpen(true)}>Add holiday</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {groups.map((g) => (
            <Box key={g.key} sx={{ ...card, p: 2.25, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: '#FFFBEB', color: '#D97706', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <EventBusyRounded />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{g.reason || 'Holiday'}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary, mt: 0.25 }}>
                    <CalendarTodayRounded sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 13 }}>{g.start_date === g.end_date ? fmtHolidayDate(g.start_date) : `${fmtHolidayDate(g.start_date)} → ${fmtHolidayDate(g.end_date)}`}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {g.queues.map((q) => <Chip key={q} size="small" label={q} sx={{ height: 22, bgcolor: colors.gray100, color: colors.gray600 }} />)}
                  </Box>
                </Box>
              </Box>
              <IconButton onClick={() => setDelTarget(g)} sx={{ border: `1px solid ${colors.border}`, borderRadius: '10px' }}><DeleteRounded /></IconButton>
            </Box>
          ))}
        </Box>
      )}

      {addOpen && <AddHolidayDialog queues={queues} onClose={() => setAddOpen(false)} onCreated={(msg) => { setAddOpen(false); setToast(msg); reload(); }} />}

      <Dialog open={!!delTarget} onClose={() => setDelTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete holiday?</DialogTitle>
        <DialogContent><Typography color="text.secondary">{delTarget?.reason || 'This holiday'} will be removed{delTarget && delTarget.ids.length > 1 ? ` for ${delTarget.ids.length} queues` : ''}.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDelTarget(null)}>Cancel</Button><Button color="error" variant="contained" onClick={doDelete}>Delete</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

/* ---------------- Add holiday dialog ---------------- */
const AddHolidayDialog: React.FC<{
  queues: { id: string; name: string }[];
  onClose: () => void;
  onCreated: (msg: string) => void;
}> = ({ queues, onClose, onCreated }) => {
  const { checkTokens, createHoliday } = useHolidays();
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<string[]>(queues.map((q) => q.id));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<CheckTokensResult | null>(null);

  // keep all selected by default as queues load
  useEffect(() => { setSelected(queues.map((q) => q.id)); }, [queues]);

  const toggleQueue = (id: string) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allOn = selected.length === queues.length && queues.length > 0;

  const params = () => ({ queue_ids: selected, start_date: start, end_date: mode === 'range' ? end : start, reason: reason.trim() || undefined });

  const validate = (): string | null => {
    if (!reason.trim()) return 'Enter a reason.';
    if (selected.length === 0) return 'Select at least one queue.';
    if (mode === 'range' && end < start) return 'End date must be on or after start date.';
    return null;
  };

  const submit = async (force: boolean) => {
    const v = validate(); if (v) { setErr(v); return; }
    setBusy(true); setErr(null);
    try {
      if (!force) {
        const check = await checkTokens(params());
        if (check.has_tokens && check.total_count > 0) { setWarn(check); setBusy(false); return; }
      }
      const res = await createHoliday(params());
      onCreated(res.total_cancelled_tokens > 0
        ? `Holiday added — ${res.total_cancelled_tokens} token(s) cancelled, customers notified`
        : 'Holiday added');
    } catch (e: any) { setErr(e?.response?.data?.message || 'Failed to add holiday.'); setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add holiday</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)} sx={{ mb: 2 }}>
          <ToggleButton value="single">Single day</ToggleButton>
          <ToggleButton value="range">Date range</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField type="date" label="Start date" InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} fullWidth />
          {mode === 'range' && <TextField type="date" label="End date" InputLabelProps={{ shrink: true }} value={end} onChange={(e) => setEnd(e.target.value)} fullWidth />}
        </Box>

        <TextField fullWidth label="Reason" placeholder="e.g. Diwali, Maintenance" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600 }}>Affected queues</Typography>
          <Button size="small" onClick={() => setSelected(allOn ? [] : queues.map((q) => q.id))}>{allOn ? 'Deselect all' : 'Select all'}</Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto' }}>
          {queues.length === 0 ? <Typography color="text.secondary" sx={{ fontSize: 14 }}>No queues found.</Typography> :
            queues.map((q) => (
              <FormControlLabel key={q.id} control={<Checkbox checked={selected.includes(q.id)} onChange={() => toggleQueue(q.id)} />} label={q.name} />
            ))}
        </Box>

        {warn && (
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mt: 2 }}>
            <b>{warn.total_count} booked token(s)</b> fall on these dates and will be cancelled. Affected customers should be notified. Proceed?
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {warn ? (
          <Button variant="contained" color="warning" disabled={busy} onClick={() => submit(true)}>{busy ? <CircularProgress size={20} color="inherit" /> : 'Proceed anyway'}</Button>
        ) : (
          <Button variant="contained" disabled={busy} onClick={() => submit(false)}>{busy ? <CircularProgress size={20} color="inherit" /> : 'Add holiday'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HolidaysPage;
